import { useState, useEffect } from 'react';
import { toast } from 'sonner';
import api from '../lib/api';
import { X, UserPlus, Spinner, CheckCircle, Warning } from '@phosphor-icons/react';

/**
 * ContactBatchPreviewDrawer — opens when Crunchy's `create_contacts` tool fires.
 * Shows the parsed contacts in a table; "Create N contacts" POSTs the batch to
 * /api/ai/contacts/bulk-create. Nothing is saved until the user approves here.
 */
export default function ContactBatchPreviewDrawer({ open, onClose, preview }) {
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (!open) return;
    setSaving(false);
    setResult(null);
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, preview, onClose]);

  const contacts = preview?.contacts || [];

  const handleCreate = async () => {
    if (saving || contacts.length === 0) return;
    setSaving(true);
    setResult(null);
    try {
      const { data } = await api.post('/ai/contacts/bulk-create', { contacts });
      setResult(data);
      const n = data?.created_count || 0;
      const dups = (data?.duplicates_skipped || []).length;
      // Lasting confirmation that survives the drawer closing
      toast.success(
        `✓ ${n} contact${n !== 1 ? 's' : ''} created${dups ? ` · ${dups} skipped (already in your CRM)` : ''}`,
        {
          duration: 10000,
          position: 'top-center',
          action: {
            label: 'View Contacts',
            onClick: () => { window.location.href = '/contacts'; },
          },
        },
      );
      if (dups === 0) {
        setTimeout(() => onClose?.(), 2500);
      }
    } catch (err) {
      setResult({ error: err.response?.data?.detail || err.message || 'Create failed' });
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[9998] flex justify-end" data-testid="contact-batch-drawer">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={saving ? undefined : onClose} />

      <div className="relative w-full max-w-[640px] h-full bg-white shadow-2xl flex flex-col">
        {/* Header */}
        <div className="px-5 py-3 bg-gradient-to-b from-[#0B5394] to-[#073763] text-white flex items-center gap-2">
          <UserPlus size={18} weight="bold" className="text-[#F39C12]" />
          <div className="flex-1">
            <div className="font-bold text-sm">Review new contacts</div>
            <div className="text-[10px] text-white/70">
              Crunchy parsed {contacts.length} contact{contacts.length !== 1 ? 's' : ''} — nothing is saved until you approve
            </div>
          </div>
          <button onClick={onClose} disabled={saving} className="text-white/80 hover:text-white p-1" data-testid="contact-batch-close">
            <X size={18} weight="bold" />
          </button>
        </div>

        {/* Table */}
        <div className="flex-1 overflow-y-auto">
          <table className="w-full text-[11px]">
            <thead className="sticky top-0 bg-slate-100 text-slate-500 uppercase tracking-wide text-[9px]">
              <tr>
                <th className="text-left px-3 py-2">Name</th>
                <th className="text-left px-3 py-2">Company</th>
                <th className="text-left px-3 py-2">Email / Phone</th>
                <th className="text-left px-3 py-2">Status · Tags</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {contacts.map((c, i) => (
                <tr key={i} data-testid="contact-batch-row">
                  <td className="px-3 py-2 font-semibold text-slate-900">
                    {c.first_name} {c.last_name}
                    {c.title && <div className="text-[10px] font-normal text-slate-500">{c.title}</div>}
                  </td>
                  <td className="px-3 py-2 text-slate-700">
                    {c.company || '—'}
                    {(c.address_city || c.address_state) && (
                      <div className="text-[10px] text-slate-400">
                        {[c.address_city, c.address_state].filter(Boolean).join(', ')}
                      </div>
                    )}
                  </td>
                  <td className="px-3 py-2 text-slate-700">
                    <div className="truncate max-w-[160px]">{c.email || '—'}</div>
                    {c.phone && <div className="text-[10px] text-slate-500">{c.phone}</div>}
                  </td>
                  <td className="px-3 py-2">
                    <span className="text-[9px] font-bold uppercase px-1.5 py-0.5 rounded bg-blue-100 text-blue-800">{c.status}</span>
                    {(c.tags || []).length > 0 && (
                      <div className="text-[9px] text-slate-500 mt-0.5">{c.tags.join(' · ')}</div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {(preview?.already_in_crm || []).length > 0 && (
            <div className="px-4 py-2.5 bg-amber-50 border-t border-amber-100" data-testid="contact-batch-existing">
              <div className="text-[10px] font-bold uppercase tracking-wide text-amber-700 mb-1">
                {preview.already_in_crm.length} already in your CRM (won&apos;t be created)
              </div>
              {preview.already_in_crm.map((a, i) => (
                <div key={i} className="text-[11px] text-amber-800">{a.matched || a.name}</div>
              ))}
              <div className="text-[10px] text-amber-600 mt-1">
                Got new info on them? Tell Crunchy to &quot;update these contacts&quot; and he&apos;ll enrich the existing records instead.
              </div>
            </div>
          )}
          {(preview?.skipped || []).length > 0 && (
            <div className="px-4 py-2 text-[10px] text-amber-700 bg-amber-50 border-t border-amber-100">
              {preview.skipped.length} entr{preview.skipped.length === 1 ? 'y' : 'ies'} skipped:{' '}
              {[...new Set(preview.skipped.map((s) => s.reason))].join(' · ')}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-slate-200 px-5 py-3 space-y-2">
          {result && !result.error && (
            <div className="flex items-center gap-2 text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-3 py-2" data-testid="contact-batch-success">
              <CheckCircle size={14} weight="fill" />
              Created {result.created_count} contact{result.created_count !== 1 ? 's' : ''}
              {(result.duplicates_skipped || []).length > 0 &&
                ` · ${result.duplicates_skipped.length} skipped (already in your CRM)`}
            </div>
          )}
          {result?.error && (
            <div className="flex items-center gap-2 text-xs text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2" data-testid="contact-batch-error">
              <Warning size={14} weight="fill" /> {result.error}
            </div>
          )}
          <div className="flex gap-2 justify-end">
            <button
              onClick={onClose}
              disabled={saving}
              className="text-xs text-slate-600 hover:text-slate-900 px-4 py-2"
              data-testid="contact-batch-cancel"
            >
              Cancel
            </button>
            <button
              onClick={handleCreate}
              disabled={saving || contacts.length === 0 || (result && !result.error)}
              className="bg-[#F39C12] hover:bg-[#E08C00] disabled:opacity-50 text-black font-bold text-xs rounded px-5 py-2 flex items-center gap-1.5"
              data-testid="contact-batch-create-btn"
            >
              {saving ? (<><Spinner size={13} className="animate-spin" /> Creating…</>)
                : (<><UserPlus size={13} weight="bold" /> Create {contacts.length} contact{contacts.length !== 1 ? 's' : ''}</>)}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
